<template>
  <v-dialog :value="true" persistent scrollable transition="scroll-y-transition">
    <v-card>
      <v-card-title v-if="!isAdded && !hasError" class="headline">Добавить место</v-card-title>

      <v-card-title v-if="isAdded" class="headline green--text">Место добавлено</v-card-title>

      <v-card-title v-if="hasError" class="headline red--text">Ошибка</v-card-title>

      <v-card-text>
        <v-form v-if="!isAdded && !hasError" ref="form">
          <v-text-field
            v-model="formData.name"
            label="Название"
            :error-messages="nameErrors"
            @input="$v.$touch()"
            @blur="$v.$touch()"
          />

          <v-select
            v-model="formData.type"
            :items="types"
            item-text="name"
            item-value="uid"
            label="Item"
            required
          />

          <v-row>
            <v-col cols="12" xs="12" sm="6" md="4" lg="6" xl="6">
              <v-text-field v-model="formData.phone" label="Телефон" required />
            </v-col>

            <v-col cols="12" xs="12" sm="6" md="4" lg="6" xl="6">
              <v-text-field v-model="formData.phoneAdditional" label="Дополнительный телефон" />
            </v-col>

            <v-col cols="12" xs="12" sm="6" md="4" lg="6" xl="6">
              <v-text-field
                v-model="formData.telegram"
                prefix="@"
                label="Telegram"
                :hint="`http://t.me/${formData.telegram}`"
                :error-messages="telegramErrors"
                @input="$v.formData.telegram.$touch()"
                @blur="$v.formData.telegram.$touch()"
              />
            </v-col>

            <v-col cols="12" xs="12" sm="6" md="4" lg="6" xl="6">
              <v-text-field v-model="formData.whatsapp" label="WhatsApp" />
            </v-col>

            <v-col cols="12" xs="12" sm="6" md="4" lg="6" xl="6">
              <v-text-field
                v-model="formData.email"
                label="Email"
                :error-messages="emailErrors"
                @input="$v.formData.email.$touch()"
                @blur="$v.formData.email.$touch()"
              />
            </v-col>

            <v-col cols="12" xs="12" sm="6" md="4" lg="6" xl="6">
              <v-text-field
                v-model="formData.site"
                label="Сайт"
                :error-messages="siteErrors"
                @input="$v.formData.site.$touch()"
                @blur="$v.formData.site.$touch()"
              />
            </v-col>

            <v-col cols="12" xs="12" sm="6" md="4" lg="6" xl="6">
              <v-text-field
                v-model="formData.instagram"
                prefix="@"
                label="Instagram"
                :error-messages="instagramErrors"
                @input="$v.formData.instagram.$touch()"
                @blur="$v.formData.instagram.$touch()"
              />
            </v-col>

            <v-col cols="12" xs="12" sm="6" md="4" lg="6" xl="6">
              <v-text-field
                v-model="formData.facebook"
                label="Facebook"
                :error-messages="facebookErrors"
                @input="$v.formData.facebook.$touch()"
                @blur="$v.formData.facebook.$touch()"
              />
            </v-col>

            <v-col cols="12" xs="12" sm="6" md="4" lg="6" xl="6">
              <v-text-field
                v-model="formData.vk"
                label="VK"
                :error-messages="vkErrors"
                @input="$v.formData.vk.$touch()"
                @blur="$v.formData.vk.$touch()"
              />
            </v-col>

            <v-col cols="12" xs="12" sm="6" md="4" lg="6" xl="6">
              <v-text-field
                v-model="formData.untappd"
                label="Untappd"
                :error-messages="untappdErrors"
                @input="$v.formData.untappd.$touch()"
                @blur="$v.formData.untappd.$touch()"
              />
            </v-col>

            <v-col cols="12" xs="12" sm="6" md="4" lg="6" xl="6">
              <v-text-field
                v-model="formData.ratebeer"
                label="RateBeer"
                :error-messages="ratebeerErrors"
                @input="$v.formData.ratebeer.$touch()"
                @blur="$v.formData.ratebeer.$touch()"
              />
            </v-col>
          </v-row>

          <v-text-field v-model="formData.address" label="Адрес" />
        </v-form>

        <template v-if="isAdded">
          <p>Спасибо! В ближайшее время точка появится на карте.</p>
        </template>

        <template v-if="hasError">
          <p>Что-то пошло не так. :(</p>

          <p>
            Напишите нам в
            <a href="https://www.facebook.com/finder.beer/" target="_blank">Facebook</a>
          </p>
        </template>

        <loader v-if="isLoading" />
      </v-card-text>

      <v-btn
        v-if="isAdded || hasError"
        color="green darken-1"
        class="mx-3 my-3"
        text
        @click="closeModal"
      >
        Закрыть
      </v-btn>

      <v-card-actions v-if="!isAdded && !hasError">
        <v-btn color="blue darken-1" text @click="closeModal">Закрыть</v-btn>

        <v-spacer />

        <v-btn color="primary" :disabled="!isFormValid" @click="add">Добавить</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { email, maxLength, minLength, required, url } from 'vuelidate/lib/validators';
import PlaceService from '@/services/PlaceService';
import Loader from '@/components/Loader';

export default {
  name: 'Create',

  components: { Loader },

  mixins: [validationMixin],

  props: {
    lat: {
      type: Number,
      required: true,
    },

    lng: {
      type: Number,
      required: true,
    },
  },

  validations: {
    formData: {
      name: {
        required,
        minLength: minLength(2),
      },

      email: {
        email,
      },

      telegram: {
        minLength: minLength(5),
        validLogin(login) {
          return /^[a-zA-z0-9_]*$/.test(login);
        },
      },

      site: {
        url,
      },

      instagram: {
        maxLength: maxLength(30),
        validLogin(login) {
          return /^[a-zA-z0-9_.]*$/.test(login);
        },
      },

      facebook: {
        validUrl(link) {
          return !link || /^https?:\/\/((www|m)\.)?facebook\.com/.test(link);
        },
      },

      vk: {
        validUrl(link) {
          return !link || /^https?:\/\/(m\.)?vk\.com/.test(link);
        },
      },

      untappd: {
        validUrl(link) {
          return !link || /^https?:\/\/(untappd\.com|untp\.beer)/.test(link);
        },
      },

      ratebeer: {
        validUrl(link) {
          return !link || /^https:\/\/(www\.)?ratebeer\.com/.test(link);
        },
      },
    },
  },

  data() {
    return {
      isAdded: false,
      hasError: false,
      isLoading: false,
      formData: {
        name: '',
        type: 'BAR',
        phone: '',
        phoneAdditional: '',
        telegram: '',
        whatsapp: '',
        email: '',
        site: '',
        instagram: '',
        facebook: '',
        vk: '',
        untappd: '',
        ratebeer: '',
        address: '',
      },

      rules: {
        name: [(v) => !!v || 'Name is required'],
      },

      types: [
        {
          name: 'Бар',
          uid: 'BAR',
        },
        {
          name: 'Бар-магазин',
          uid: 'BAR_SHOP',
        },
        {
          name: 'Магазин',
          uid: 'SHOP',
        },
      ],
    };
  },

  computed: {
    isFormValid() {
      return !this.$v.$invalid;
    },

    nameErrors() {
      const errors = [];
      if (!this.$v.formData.name.$dirty) return errors;
      if (!this.$v.formData.name.minLength) errors.push('Введите название.');
      if (!this.$v.formData.name.required) errors.push('Название слишком короткое.');
      return errors;
    },

    telegramErrors() {
      const errors = [];
      if (!this.$v.formData.telegram.$dirty) return errors;
      if (!this.$v.formData.telegram.minLength) errors.push('Неправильный логин');
      if (!this.$v.formData.telegram.validLogin) errors.push('Неправильный логин');
      return errors;
    },

    emailErrors() {
      const errors = [];
      if (!this.$v.formData.email.$dirty) return errors;
      if (!this.$v.formData.email.email) errors.push('Неправильный адрес');
      return errors;
    },

    siteErrors() {
      const errors = [];
      if (!this.$v.formData.site.$dirty) return errors;
      if (!this.$v.formData.site.url) errors.push('Неправильный адрес сайта');
      return errors;
    },

    instagramErrors() {
      const errors = [];
      if (!this.$v.formData.instagram.$dirty) return errors;
      if (!this.$v.formData.instagram.maxLength) errors.push('Неправильный логин');
      if (!this.$v.formData.instagram.validLogin) errors.push('Неправильный логин');
      return errors;
    },

    facebookErrors() {
      const errors = [];
      if (!this.$v.formData.facebook.$dirty) return errors;
      if (!this.$v.formData.facebook.validUrl) errors.push('Неправильная ссылка');
      return errors;
    },

    vkErrors() {
      const errors = [];
      if (!this.$v.formData.vk.$dirty) return errors;
      if (!this.$v.formData.vk.validUrl) errors.push('Неправильная ссылка');
      return errors;
    },

    untappdErrors() {
      const errors = [];
      if (!this.$v.formData.untappd.$dirty) return errors;
      if (!this.$v.formData.untappd.validUrl) errors.push('Неправильная ссылка');
      return errors;
    },

    ratebeerErrors() {
      const errors = [];
      if (!this.$v.formData.ratebeer.$dirty) return errors;
      if (!this.$v.formData.ratebeer.validUrl) errors.push('Неправильная ссылка');
      return errors;
    },
  },

  methods: {
    closeModal() {
      this.$emit('close');
      this.$store.dispatch('map/disableAddMode');
    },

    add() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.isAdded = false;
      this.hasError = false;
      this.isLoading = true;

      PlaceService.create({ ...this.formData, lat: this.lat, lon: this.lng })
        .then(() => {
          this.isAdded = true;
          this.$store.dispatch('map/disableAddMode');
        })
        .catch(() => {
          this.hasError = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    validate() {
      this.$refs.form.validate();
    },

    reset() {
      this.$refs.form.reset();
    },

    resetValidation() {
      this.$refs.form.resetValidation();
    },
  },
};
</script>

<style scoped></style>
